// Reference data — the 21 platform languages, fallback channels, fallback
// guides. Mirrors mobile/src/config/* and the design's web-data.jsx so we
// have something to render before the API responds (and during onboarding).

// Live App Store listing for the iOS app (Apple app id 6761561220). Region-
// neutral `/app/idNNN` form so Apple auto-redirects each visitor to their
// local storefront. Used by the landing-page download badges. The Google
// Play listing is not live yet — the Play badge renders as a non-clickable
// "coming soon" placeholder.
export const APP_STORE_URL = 'https://apps.apple.com/app/id6761561220';

export interface PlatformLanguage {
  readonly code: string;
  readonly name: string;
  readonly native: string;
  readonly flag: string;
}

export const PLATFORM_LANGUAGES: readonly PlatformLanguage[] = [
  { code: 'en', name: 'English', native: 'English', flag: '🇬🇧' },
  { code: 'sv', name: 'Swedish', native: 'Svenska', flag: '🇸🇪' },
  { code: 'de', name: 'German', native: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'French', native: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Spanish', native: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italian', native: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', native: 'Português', flag: '🇵🇹' },
  { code: 'nl', name: 'Dutch', native: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polish', native: 'Polski', flag: '🇵🇱' },
  { code: 'fi', name: 'Finnish', native: 'Suomi', flag: '🇫🇮' },
  { code: 'no', name: 'Norwegian', native: 'Norsk', flag: '🇳🇴' },
  { code: 'da', name: 'Danish', native: 'Dansk', flag: '🇩🇰' },
  { code: 'ja', name: 'Japanese', native: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', native: '한국어', flag: '🇰🇷' },
  { code: 'zh', name: 'Chinese', native: '中文', flag: '🇨🇳' },
  { code: 'ar', name: 'Arabic', native: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी', flag: '🇮🇳' },
  { code: 'tr', name: 'Turkish', native: 'Türkçe', flag: '🇹🇷' },
  { code: 'ru', name: 'Russian', native: 'Русский', flag: '🇷🇺' },
  { code: 'uk', name: 'Ukrainian', native: 'Українська', flag: '🇺🇦' },
  { code: 'el', name: 'Greek', native: 'Ελληνικά', flag: '🇬🇷' },
];

export function findLanguage(code: string | null | undefined): PlatformLanguage {
  return PLATFORM_LANGUAGES.find((l) => l.code === code) ?? PLATFORM_LANGUAGES[0]!;
}

// Superset of language code → English name. Mirrors mobile/src/config/
// constants.ts LANGUAGE_NAMES — articles can be translated FROM any
// source language (even ones we don't actively translate INTO), so the
// pill needs to label codes that aren't in PLATFORM_LANGUAGES.
const LANGUAGE_NAMES: Readonly<Record<string, string>> = {
  en: 'English',
  sv: 'Swedish',
  ar: 'Arabic',
  fi: 'Finnish',
  bs: 'Bosnian',
  hi: 'Hindi',
  uk: 'Ukrainian',
  th: 'Thai',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
  it: 'Italian',
  pt: 'Portuguese',
  nl: 'Dutch',
  ja: 'Japanese',
  zh: 'Chinese',
  ko: 'Korean',
  ru: 'Russian',
  tr: 'Turkish',
  pl: 'Polish',
  da: 'Danish',
  no: 'Norwegian',
  el: 'Greek',
  fa: 'Persian',
  vi: 'Vietnamese',
};

export function getLanguageName(code: string | null | undefined): string {
  if (!code) return '';
  return LANGUAGE_NAMES[code] ?? code.toUpperCase();
}

export interface FallbackChannel {
  readonly id: string;
  readonly name: string;
  readonly lang: string;
  readonly country: string;
}

export const FALLBACK_CHANNELS: readonly FallbackChannel[] = [
  { id: 'svt', name: 'SVT Nyheter', lang: 'SV', country: 'Sweden' },
  { id: 'bbc', name: 'BBC News', lang: 'EN', country: 'United Kingdom' },
  { id: 'lem', name: 'Le Monde', lang: 'FR', country: 'France' },
  { id: 'der', name: 'Der Spiegel', lang: 'DE', country: 'Germany' },
  { id: 'aje', name: 'Al Jazeera', lang: 'EN', country: 'Qatar' },
  { id: 'asa', name: 'Asahi Shimbun', lang: 'JA', country: 'Japan' },
  { id: 'pai', name: 'El País', lang: 'ES', country: 'Spain' },
  { id: 'cor', name: 'Corriere', lang: 'IT', country: 'Italy' },
];

export const FEED_TOPICS: readonly string[] = [
  'For you',
  'World',
  'Politics',
  'Housing',
  'Visa',
  'Work',
  'Tax',
  'Tech',
  'Culture',
  'Sports',
];

export interface FallbackGuide {
  readonly id: string;
  readonly cat: string;
  readonly icon: 'home' | 'shield' | 'book' | 'target' | 'globe' | 'plane';
  readonly title: string;
  readonly sub: string;
  readonly tone: 'amber' | 'blue' | 'green' | 'red' | 'purple';
}

export const FALLBACK_GUIDES: readonly FallbackGuide[] = [
  {
    id: 'g1',
    cat: 'Housing',
    icon: 'home',
    title: 'Renting in Stockholm: queues, contracts, and second-hand deals',
    sub: 'A practical map of Bostad Stockholm, andrahand, and what your contract really says — translated and explained.',
    tone: 'amber',
  },
  {
    id: 'g2',
    cat: 'Visa',
    icon: 'shield',
    title: 'EU Blue Card 2026 walkthrough',
    sub: 'New thresholds, fast-track professions, and what changes for family reunification this year.',
    tone: 'blue',
  },
  {
    id: 'g3',
    cat: 'Tax',
    icon: 'book',
    title: 'Filing income taxes across two countries',
    sub: 'Double-tax treaties, residency rules, and how to read your Swedish K-form alongside an HMRC return.',
    tone: 'green',
  },
  {
    id: 'g4',
    cat: 'Healthcare',
    icon: 'target',
    title: 'Getting a personnummer and your first vårdcentral visit',
    sub: 'The order of operations, what to bring, and translated forms for the most common appointments.',
    tone: 'red',
  },
  {
    id: 'g5',
    cat: 'Banking',
    icon: 'globe',
    title: 'Opening a bank account without a personnummer',
    sub: 'Which Swedish banks accept samordningsnummer, what proofs they want, and timelines.',
    tone: 'blue',
  },
  {
    id: 'g6',
    cat: 'Schools',
    icon: 'plane',
    title: 'International schools vs. friskolor in greater Stockholm',
    sub: 'Curricula, cost, language support, and how the lottery actually works.',
    tone: 'purple',
  },
];

export const GUIDE_CATEGORIES = ['All', 'Housing', 'Visa', 'Tax', 'Healthcare', 'Banking', 'Schools'] as const;

// Lightweight country directory — covers the markets the mobile app targets.
// Mirrors `mobile/src/config/constants.ts` (COUNTRY_NAMES + COUNTRY_FLAGS).
export const COUNTRY_NAMES: Readonly<Record<string, string>> = {
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  GB: 'United Kingdom',
  IE: 'Ireland',
  DE: 'Germany',
  FR: 'France',
  ES: 'Spain',
  IT: 'Italy',
  NL: 'Netherlands',
  BE: 'Belgium',
  PT: 'Portugal',
  PL: 'Poland',
  GR: 'Greece',
  US: 'United States',
  CA: 'Canada',
  AU: 'Australia',
  IN: 'India',
  JP: 'Japan',
  CN: 'China',
  KR: 'South Korea',
  BR: 'Brazil',
  MX: 'Mexico',
  TR: 'Turkey',
  RU: 'Russia',
  UA: 'Ukraine',
  AE: 'United Arab Emirates',
  ZA: 'South Africa',
  EG: 'Egypt',
};

const COUNTRY_FLAGS: Readonly<Record<string, string>> = {
  SE: '🇸🇪', NO: '🇳🇴', DK: '🇩🇰', FI: '🇫🇮', GB: '🇬🇧', IE: '🇮🇪',
  DE: '🇩🇪', FR: '🇫🇷', ES: '🇪🇸', IT: '🇮🇹', NL: '🇳🇱', BE: '🇧🇪',
  PT: '🇵🇹', PL: '🇵🇱', GR: '🇬🇷', US: '🇺🇸', CA: '🇨🇦', AU: '🇦🇺',
  IN: '🇮🇳', JP: '🇯🇵', CN: '🇨🇳', KR: '🇰🇷', BR: '🇧🇷', MX: '🇲🇽',
  TR: '🇹🇷', RU: '🇷🇺', UA: '🇺🇦', AE: '🇦🇪', ZA: '🇿🇦', EG: '🇪🇬',
};

export function getFlag(country: string | null | undefined): string {
  if (!country) return '';
  return COUNTRY_FLAGS[country] ?? '🏳️';
}

export function getCountryName(country: string | null | undefined): string | null {
  if (!country) return null;
  return COUNTRY_NAMES[country] ?? country;
}

// Demo articles used on the marketing landing page only — they show the
// reader experience to logged-out visitors. The authenticated feed always
// pulls from the backend `/feed` endpoint.
export interface DemoArticle {
  readonly id: string;
  readonly channelName: string;
  readonly timeAgo: string;
  readonly title: string;
  readonly bullets: string[];
  readonly tags: string[];
  readonly readTime: number;
  readonly isTranslated: boolean;
  readonly translatedFrom: string | null;
  readonly imageUrl: string;
}

export const DEMO_ARTICLES: readonly DemoArticle[] = [
  {
    id: 'a1',
    channelName: 'SVT Nyheter',
    timeAgo: '2H AGO',
    title: 'Stockholm tightens rental rules as expat housing crunch deepens',
    bullets: [
      'New rules cap short-term lets in inner Stockholm from September.',
      'Landlords must register with Hyresnämnden or risk SEK 50,000 fines.',
      'City says 14,000 expats on waiting list as of Q1.',
    ],
    tags: ['housing', 'sweden', 'expat-life'],
    readTime: 5,
    isTranslated: true,
    translatedFrom: 'sv',
    imageUrl: 'https://images.unsplash.com/photo-1509356843151-3e7d96241e11?w=900&auto=format&fit=crop',
  },
  {
    id: 'a2',
    channelName: 'BBC News',
    timeAgo: '5H AGO',
    title: 'EU blue card changes take effect — what non-EU workers need to know',
    bullets: [
      'Minimum salary threshold drops by 11% across most member states.',
      'New fast-track covers regulated professions in health and IT.',
      'Family reunification timelines shorten to 6 weeks in practice.',
    ],
    tags: ['visa', 'eu', 'work'],
    readTime: 6,
    isTranslated: false,
    translatedFrom: null,
    imageUrl: 'https://images.unsplash.com/photo-1488603195819-7c5152c1ab5b?w=900&auto=format&fit=crop',
  },
  {
    id: 'a3',
    channelName: 'Le Monde',
    timeAgo: '1D AGO',
    title: 'Paris unveils cross-border tax framework for remote freelancers',
    bullets: [
      'Single filing covers income earned in multiple EU jurisdictions.',
      'Applies to freelancers billing clients in 2+ countries per year.',
      'Rolls out in January; pilot in Île-de-France already underway.',
    ],
    tags: ['tax', 'france', 'remote-work'],
    readTime: 7,
    isTranslated: true,
    translatedFrom: 'fr',
    imageUrl: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=900&auto=format&fit=crop',
  },
];
